import '@Resources/fonts/fonts.css';
import '@Resources/styles/app.css';
import LayoutStyles from '@Styles/layout.scss';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import {
    DataGridPro,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridAutosizeOptions,
    GridColDef,
    GridColumnResizeParams,
    GridColumnVisibilityModel,
    GridDensity,
    GridFilterModel,
    GridRenderCellParams,
    GridRowHeightParams,
    GridRowSelectionModel,
    GridSortModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    gridClasses,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license';
import { parseISO } from 'date-fns';
import _isNil from 'lodash/isNil';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FolderOption } from '@Components/FolderComponents/FolderOption';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import {
    expandedFolderIdsState,
    isLoadingFoldersState,
    qcFoldersState,
} from '@Data/folders/Reducer';
import { QcFolderMenu } from './FolderMenu';

LicenseInfo.setLicenseKey(MUI_X_LICENSE);

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <Box sx={{ flexGrow: 1 }} />
            <GridToolbarExport
                slotProps={{
                    tooltip: { title: 'Export data' },
                    button: { variant: 'outlined' },
                }}
            />
        </GridToolbarContainer>
    );
}

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    fontFamily: 'Barlow Semi Condensed',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '11px',

    letterSpacing: '0.02em',
    color: '#1a1e2c',
}));

const FoldersDataGrid = (props) => {
    const {
        linkDepartmentProcess,
        handleClick,
        onViewFiles,
        onJobSettingsClick,
        handleOpenFolderStatusDialog,
        handleProcessFolder,
        handleAutoQc,
        handleShareFolder,
        handleCreateSubset,
        handleClearVisibleFolders,
    } = props;

    const [rowSelectionModel, setRowSelectionModel] =
        React.useState<GridRowSelectionModel>([]);

    const [density, setDensity] = React.useState<GridDensity>('compact');

    const expandedIds: Array<string> = useSelector(expandedFolderIdsState);

    const folderOptionsList = useSelector(qcFoldersState);
    const isLoadingFolders = useSelector(isLoadingFoldersState);

    const apiRef = useGridApiRef();

    const [columnVisibilityModel, setColumnVisibilityModel] =
        React.useState<GridColumnVisibilityModel>({
            sow_name: false,
            folder_last_io_sync_time: false,
            folder_due_date: false,
        });
    const [columnsortModel, setColumnSortModel] = React.useState<GridSortModel>(
        [{ field: 'folder_name', sort: 'desc' }]
    );

    const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
        items: [
            {
                field: 'folder_name',
                operator: 'contains',
            },
        ],
    });
    const [autosizeOptions, setAutosizeOptions] =
        React.useState<GridAutosizeOptions>({
            expand: true,
            includeOutliers: true,
            includeHeaders: true,
        });

    const [autosizeDisabled, setAutosizeDisabled] =
        React.useState<boolean>(false);

    function createFolderColumnsDefintion(): GridColDef[] {
        return [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                sortable: true,
                sortingOrder: ['desc', 'asc', null],
                disableColumnMenu: true,
                width: 70,
            },
            {
                field: 'pole_number',
                headerName: 'Poles',
                disableColumnMenu: true,
                width: 60,
            },
            {
                field: 'folder',
                headerName: '',
                disableColumnMenu: true,
                filterable: false,

                sortComparator: (
                    v1: any,
                    v2: any,
                    param1: { value: { folder_name: any } },
                    param2: { value: { folder_name: string } }
                ) =>
                    `${param1.value.folder_name.toLocaleLowerCase()}`.localeCompare(
                        param2.value.folder_name.toLocaleLowerCase()
                    ),

                getOptionValue: (value: any) => value?.folder_name,
                renderCell: (props: GridRenderCellParams<any, IFolder>) => {
                    let { hasFocus, value } = props;
                    hasFocus = value.isSelected || hasFocus;

                    return (
                        <FolderOption
                            gridRef={apiRef}
                            folder={value}
                            columns={columns}
                            setColumns={setColumns}
                            linkDepartmentProcess={linkDepartmentProcess}
                            key={`fo-${value?.folder_id}`}
                            onClick={() => handleClick(value)}
                            isInProgress={props.row.isInProgress}
                            folderMenu={
                                // Note this is not the qcsnapshot folder menu
                                <QcFolderMenu
                                    id={`${value?.folder_id}-qc-folder-menu`}
                                    folder={value}
                                    selectedFolder={value}
                                    onViewFiles={onViewFiles}
                                    onAutoQc={handleAutoQc}
                                    onJobSettingsClick={onJobSettingsClick}
                                    onSetStatus={handleOpenFolderStatusDialog}
                                    onProcess={handleProcessFolder}
                                    onShare={handleShareFolder}
                                    onCreateSubset={handleCreateSubset}
                                />
                            }
                        />
                    );
                },
            },
            {
                field: 'folder_name',
                headerName: 'Folder Name',
                minWidth: 180,
                disableColumnMenu: true,
            },
            {
                field: 'status',
                headerName: 'Status',
                disableColumnMenu: true,
                width: 90,
            },
            {
                field: 'folder_target_date',
                headerName: 'Target Date',
                type: 'date',
                disableColumnMenu: true,
                width: 90,
            },

            {
                field: 'assigned_to',
                headerName: 'Assigned To',
                disableColumnMenu: true,
                width: 120,
            },

            {
                field: 'assigned_qc_analyst',
                headerName: 'QC analyst',
                disableColumnMenu: true,
                width: 120,
            },
            {
                field: 'note',
                headerName: 'Note',
                disableColumnMenu: true,
                width: 90,
            },

            {
                field: 'sow_name',
                headerName: 'SoW',
                disableColumnMenu: true,
                width: 90,
            },

            {
                field: 'folder_due_date',
                headerName: 'Due Date',
                type: 'date',
                disableColumnMenu: true,
                width: 90,
            },
            {
                field: 'folder_last_io_sync_time',
                headerName: 'IO Sync (m)',
                disableColumnMenu: true,
                width: 90,
            },
        ];
    }

    const [columns, setColumns] = React.useState<GridColDef[]>(
        createFolderColumnsDefintion
    );

    const handleRowEvent = (rowSelectionModel: GridRowSelectionModel) => {
        handleClearVisibleFolders();
        const folders = rowSelectionModel.map((row: string) => {
            return folderOptionsList.find((f) => f.folder_id === row);
        });
        const folderIds = rowSelectionModel.map((row: string) => {
            return folderOptionsList.find((f) => f.folder_id === row).folder_id;
        });
        if (folders.length > 0) {
            let firstFolder = folderIds[0];
            FoldersApi.setSelectedFolder(firstFolder);
            FoldersApi.setSelectedFolderIds(folderIds);
            FoldersApi.setMultipleFolderVisibility(folders, () => {
                FoldersApi.zoomToBounds(firstFolder, folderIds);
            });
        }
    };

    const component = (
        <>
            <div
                style={{
                    height: `calc(100vh - ${LayoutStyles.headerHeight})`,
                    width: '1000px',
                }}
            >
                <StyledDataGrid
                    rows={folderOptionsList}
                    loading={isLoadingFolders}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newRowSelectionModel: any) => {
                        setRowSelectionModel(newRowSelectionModel);
                        handleRowEvent(newRowSelectionModel);
                    }}
                    autosizeOptions={autosizeOptions}
                    disableAutosize={autosizeDisabled}
                    columns={columns}
                    density={density}
                    apiRef={apiRef}
                    autosizeOnMount={false}
                    onColumnWidthChange={(
                        params: GridColumnResizeParams,
                        event,
                        details
                    ) => {
                        if (columns && columns.length > 0) {
                            let item = columns.find(
                                (c) => c.field === params.colDef.field
                            );
                            if (item.width != params.width) {
                                item.width = params.width;
                            }
                        }
                    }}
                    disableColumnResize={false}
                    onDensityChange={(newDensity: any) =>
                        setDensity(newDensity)
                    }
                    checkboxSelection={true}
                    disableMultipleRowSelection={false}
                    rowHeight={50}
                    getRowHeight={({
                        id,
                        densityFactor,
                    }: GridRowHeightParams) => {
                        if (expandedIds.includes(String(id))) {
                            return 'auto';
                        } else {
                            return null;
                        }
                    }}
                    disableRowSelectionOnClick={true}
                    filterModel={filterModel}
                    onFilterModelChange={(newModel) => setFilterModel(newModel)}
                    sortModel={columnsortModel}
                    onSortModelChange={(newModel) =>
                        setColumnSortModel(newModel)
                    }
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    filterDebounceMs={500}
                    hideFooterPagination={true}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(
                        newModel: GridColumnVisibilityModel
                    ) => {
                        setColumnVisibilityModel(newModel);
                    }}
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            border: 'none',
                        },
                        [`& .${gridClasses.cell}`]: {
                            display: 'grid',
                            alignItems: 'center',
                            border: 'none',
                            whiteSpace: 'nowrap !important',
                        },
                        [`& .${gridClasses.row}`]: {
                            whiteSpace: 'nowrap',
                        },
                        [`& .${gridClasses.main}`]: {
                            overflowX: 'scroll',
                            overflowY: 'hidden',
                        },
                    }}
                />
            </div>
        </>
    );

    return component;
};
export default FoldersDataGrid;
